import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { colors } from '../../utils/colors';

interface InputPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (newName: string) => void;
  buttonText: string;
  dialogTitle: string;
  dialogText: string;
  inputValue: string;
}

const InputPopup: React.FC<InputPopupProps> = ({
  open,
  onClose,
  onSubmit,
  buttonText,
  dialogTitle,
  dialogText,
  inputValue,
}) => {
  const [value, setValue] = useState(inputValue);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setValue(inputValue);
      setError('');
    }
  }, [open, inputValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    if (newValue.length < 3) {
      setError(t('app.labels.MustBeAtLeast3CharactersLong'));
    } else {
      setError('');
    }
  };

  const handleSubmit = () => {
    if (value.length >= 3) {
      onSubmit(value);
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // Prevent default form submission
    handleSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="tablet" fullWidth>
      <form onSubmit={handleFormSubmit}>
        <DialogTitle id="input-popup-title">
          <Typography variant="h2">{dialogTitle}</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <DialogContentText id="input-popup-description">
              <Typography sx={{ color: colors.text.primary }}>{dialogText}</Typography>
            </DialogContentText>
            <TextField
              autoFocus
              onFocus={(e) => {
                e.target.select();
              }}
              margin="dense"
              id="name"
              type="text"
              fullWidth
              variant="outlined"
              value={value}
              onChange={handleChange}
              error={!!error}
              helperText={error}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2, gap: 1 }}>
          <Button
            type="submit"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ color: 'primary.main', minWidth: '100px' }}
            disabled={value.length < 3}
          >
            {buttonText}
          </Button>
          <Button onClick={onClose} sx={{ minWidth: '100px' }}>
            {t('app.buttons.cancel')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InputPopup;
